import { css } from "@linaria/core";
import React, { useMemo } from "react";
import { spacings } from "@ui/Theme";
import type { Project } from "../../../../api/useProjects";
import { type Quantity } from "../../../../api/useQuantities";
import useSeries from "../../Events/hooks/charts/averageLosses/useSeries";
import useOptions from "../../Events/hooks/charts/averageLosses/useOptions";
import { useSetups } from "../../../../api/useSetups";
import { type ActiveAnalysis } from "../../../../api/useActiveAnalysesStream";
import HighchartsComponent from "@ui/Highcharts";
import { FaultEventLoading } from "../../Events/components/FaultEventLoading";
import NoDataPeriodPlaceholderWidget from "../NoDataPeriodPlaceholderWidget";
import type { PointOptionsObject } from "highcharts";
import { computeInternalGapsOverPeriodActiveAnalyses } from "../../../../domain/aggregation/internalGapOverPeriod";
import { useMetrologyOverrides } from "../../../../api/useMetrologyOverrides";
import StatisticAverageLosses from "./StatisticAverageLosses";
import { useActiveAnalysesPeriod } from "../../Events/hooks/useAnalysesPeriod";
import { WidgetLegend } from "../WidgetLegend";
import { PointSymbol } from "@ui/Highcharts/ui/PointSymbol";
import seriesColors from "../../Chart/series/seriesColors";
import Marker from "@ui/Highcharts/ui/Marker";
import t from "../../../../i18n";
import usePostLatestAnalysisPlotBand from "../../Chart/hooks/usePostLatestAnalysisPlotBand";
import { getLatestActiveAnalysisDate } from "../../latestActiveAnalysisDate";

const ChartAverageLosses = ({
  project,
  performanceIndicator,
  activeAnalyses,
  aggregationInterval,
  previousAggregationInterval,
  loading,
}: {
  project: Project;
  performanceIndicator: Quantity;
  activeAnalyses: ActiveAnalysis[];
  aggregationInterval: { from: Date; to: Date };
  previousAggregationInterval: { from: Date; to: Date };
  loading?: boolean;
}) => {
  const chartRef = React.useRef<Highcharts.Chart>(null);
  const yAxisId = useMemo(
    () =>
      `project-id-${project.id}-faultEvent-${
        performanceIndicator === null
          ? "averageLosses"
          : `averageLosses-qoi-id-${performanceIndicator.id}`
      }`,
    [project, performanceIndicator],
  );

  const setups = useSetups(project.id);
  const metrologyOverrides = useMetrologyOverrides(project.id);
  const { activeAnalysesPeriod } = useActiveAnalysesPeriod(aggregationInterval, activeAnalyses);

  const meanInternalGap = useMemo(() => {
    const isComputing = loading === false && performanceIndicator !== null;

    if (isComputing) {
      return computeInternalGapsOverPeriodActiveAnalyses(
        performanceIndicator,
        setups,
        metrologyOverrides,
        activeAnalysesPeriod,
      );
    }

    return null;
  }, [performanceIndicator, setups, metrologyOverrides, activeAnalysesPeriod, loading]);

  const { activeAnalysesPeriod: previousActiveAnalysesPeriod } = useActiveAnalysesPeriod(
    previousAggregationInterval,
    activeAnalyses,
  );

  const previousMeanInternalGap = useMemo(
    () =>
      loading === false && performanceIndicator !== null
        ? computeInternalGapsOverPeriodActiveAnalyses(
            performanceIndicator,
            setups,
            metrologyOverrides,
            previousActiveAnalysesPeriod,
          )
        : null,
    [performanceIndicator, previousActiveAnalysesPeriod, setups, metrologyOverrides, loading],
  );

  const series = useSeries({
    project,
    yAxisId,
    setups,
    analyses: activeAnalysesPeriod,
    performanceIndicator,
  });
  const options = useOptions({
    minPeriod: aggregationInterval.from.getTime(),
    maxPeriod: aggregationInterval.to.getTime(),
    yAxisId,
    series,
    timeZone: project.timezone,
  });

  const hasData = useMemo(
    () =>
      activeAnalysesPeriod.length > 0 &&
      ((series[0]?.data ?? []) as PointOptionsObject[]).filter(
        f => f.x !== undefined && f.y !== undefined,
      ).length > 0,
    [activeAnalysesPeriod, series],
  );
  const latestActiveAnalysisDate = useMemo(() => getLatestActiveAnalysisDate(project), [project]);

  usePostLatestAnalysisPlotBand({
    chartRef,
    latestDate: latestActiveAnalysisDate,
  });

  if (loading) return <FaultEventLoading className={extension} />;

  if (!hasData || meanInternalGap === null)
    return <NoDataPeriodPlaceholderWidget className={extension} />;

  const previousMeanLosses = previousMeanInternalGap?.meanLosses ?? null;

  return (
    <>
      {project.showPartLoadSensitiveKpis && (
        <StatisticAverageLosses
          performanceIndicator={performanceIndicator}
          meanLosses={meanInternalGap.meanLosses}
          meanMeasuredLosses={meanInternalGap.meanMeasure}
          previousMeanLosses={previousMeanLosses}
        />
      )}
      <div className={sparkline}>
        <HighchartsComponent
          name="Average losses widget"
          ref={chartRef}
          options={options}
          selectablePoints={false}
        />
        <WidgetLegend>
          <Marker name={t("commons.quantities.selectFilter.filters.activation")}>
            <PointSymbol
              height={9}
              width={9}
              fill={seriesColors.sparklineMarker}
              color={seriesColors.sparklineMarker}
            />
          </Marker>
        </WidgetLegend>
      </div>
    </>
  );
};
export default ChartAverageLosses;

//#region CSS
const extension = css`
  flex: 1;
`;
const sparkline = css`
  display: flex;
  flex-direction: column;
  margin-top: ${spacings.m};
  flex: 1;
  min-height: 76px;
  min-width: 368px;
`;
//#endregion CSS
